import React, { useEffect, useState } from 'react';
import { Tag, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { jsonApi } from '@/lib/jsonApi';
import { Panel } from '@/components/responseDetail/Panel';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// Existing Gap Library tags (kept as-is; separate from Attribute & Factor tags).
export default function GapLibraryPanel({ responseId, initialIds }) {
  const [gaps, setGaps] = useState(null);
  const [ids, setIds] = useState(initialIds || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { jsonApi('/gaps').then((d) => setGaps(d.gaps || [])).catch(() => setGaps([])); }, []);
  const save = async (next) => {
    setSaving(true);
    try { await jsonApi(`/responses/${responseId}/gaps`, 'PATCH', { gap_ids: next }); setIds(next); }
    catch (e) { toast.error(e.message || 'Failed to update gaps'); } finally { setSaving(false); }
  };
  if (!gaps || (!gaps.length && !ids.length)) return null;
  const byId = Object.fromEntries(gaps.map((g) => [g.gap_id, g]));
  return (
    <Panel title="Gap Library" icon={Tag} testId="response-tag-gap"
      action={(
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="inline-flex items-center gap-1 text-xs border border-[#CBD5E1] text-[#475569] hover:border-[#1A3E6F] hover:text-[#1A3E6F] rounded-lg px-2.5 py-1.5" data-testid="add-gap-tag-btn"><Plus className="w-3.5 h-3.5" /> Add gap</button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 p-1 max-h-64 overflow-y-auto" data-testid="gap-picker">
            {gaps.map((g) => {
              const on = ids.includes(g.gap_id);
              return (
                <button key={g.gap_id} type="button" disabled={saving} onClick={() => save(on ? ids.filter((x) => x !== g.gap_id) : [...ids, g.gap_id])}
                  className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-[#F8FAFC] ${on ? 'text-[#1A3E6F] font-semibold' : 'text-[#0F172A]'}`} data-testid={`gap-picker-option-${g.gap_id}`}>
                  {on ? '✓ ' : ''}{g.name}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>
      )}>
      <div className="flex flex-wrap gap-1.5" data-testid="response-gap-pills">
        {!ids.length && <span className="text-xs text-[#94A3B8]">No gaps tagged yet.</span>}
        {ids.map((gid) => (
          <span key={gid} className="inline-flex items-center gap-1 text-xs bg-[#1A3E6F] text-white rounded-full pl-2.5 pr-1.5 py-0.5" data-testid={`selected-gap-${gid}`}>
            {byId[gid]?.name || gid}
            <button type="button" disabled={saving} onClick={() => save(ids.filter((x) => x !== gid))} className="p-0.5 rounded-full hover:bg-white/15" aria-label="Remove gap" data-testid={`remove-gap-${gid}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    </Panel>
  );
}
